import Service from '@ember/service';
import { service } from '@ember/service';
import { tracked, cached } from '@glimmer/tracking';
import { setUserProperty, clearUser } from 'tio-common/utils/datadog';
import ENV from 'tio-employee/config/environment';
import type SessionService from 'tio-employee/services/session';
import type ZendeskService from 'tio-employee/services/zendesk';
// This is for types. Anything we have custom may show an error.
import type { Store as StoreService } from '@warp-drive/core';
// Imported as a value (not type-only): `new UserModel()` instantiates the
// extension class as a placeholder before the real record loads.
import { UserExtension as UserModel } from 'tio-data/data/resources/user.ext';
import type { Company } from 'tio-data/data/resources/company.type';
import type { RoleViewExtension as RoleViewModel } from 'tio-data/data/resources/role-view.ext';
import type RouterService from 'tio-employee/services/router';
import type PreRegisterService from 'tio-employee/services/pre-register';
import type { EmployeeExtension as EmployeeModel } from 'tio-data/data/resources/employee.ext';
import type { Agreement as AgreementModel } from 'tio-data/data/resources/agreement.type';
import { trackedArray } from '@ember/reactive/collections';
import type { PersonExtension as PersonModel } from 'tio-data/data/resources/person.ext';
import { getSubdomain, getPartnerLogoUrl, isLocalhost } from 'tio-common/utils/hostname';
import { clearDemoSession } from 'tio-employee/utils/career-pathways-demo-data';
import { findRecord } from '@warp-drive/utilities/json-api';
import { meRequest } from 'tio-data/requests/me';

/**
 * The full include list needed to bootstrap a tio-employee session —
 * mirrors the legacy meIncludes array previously threaded through
 * the raw fetch in this service's `load`. App-specific (tio-agent
 * bootstraps a different graph), so it lives at the call site and is
 * passed into the shared `meRequest` builder.
 */
const meIncludes = [
  'person.employees.company',
  'person.dependent.employee.company.company-setting',
  'user-roles.relationships.jurisdiction.company.company-setting.company-lpn-settings.lpn-page',
  'logins',
  'usage-agreement',
  'privacy-agreement',
  'custom-register-agreements.condition',
  'email-addresses',
  'employees.person',
  'employees.jurisdictive-relationships',
  'employees.company.pslf-groups',
  'employees.company.company-setting',
  'employees.scholarships',
  'employees.plan',
  // The plan's selected eligibility conditions drive the SLR verification
  // guidelines + the benefit drawer's requirements section; without this
  // include `plan.eligibilityConditions` is always empty.
  'employees.plan.eligibility-conditions',
  'employees.match-participant.match-plan',
  'employees.tas-participant',
  'employees.tas-eligibilities',
  'employees.employee-addition',
  'employees.pslf-profile',
];

/**
 * Contains in-memory data and actions relevant to a user's session (as opposed
 * to the persisted data kept in the `session` service).
 *
 * @memberof services
 */
class SessionContextService extends Service {
  @service declare router: RouterService;
  @service declare session: SessionService;
  @service declare store: StoreService;
  @service declare zendesk: ZendeskService;
  @service declare preRegister: PreRegisterService;

  /**
   * Internal property to make `currentRole` reactive; may not always represent
   * the actual ID of the current role (may be undefined)
   */
  get currentRoleId() {
    return localStorage.getItem('role') ?? undefined;
  }

  /**
   * The user object of the currently logged-in user, or an empty user object if
   * the user has not yet been loaded into the session context.
   */
  @tracked user!: UserModel;

  /**
   * The company associated with the current session context.
   */
  @tracked company?: Company;

  /**
   * An array of role objects of the currently logged-in user.
   *
   * NOTE: This is not actually the roles of the current user, but rather a
   *       filtered view. Research why this is the case. [twl 2.Aug.23]
   */
  roles: RoleViewModel[] = trackedArray([]);

  @tracked actualCurrentRelationshipType?: string;

  constructor() {
    // eslint-disable-next-line prefer-rest-params
    super(...arguments);
    this.user = new UserModel();
    this.actualCurrentRelationshipType = this.currentRoleId;
  }

  /**
   * Whether the authenticated user and relationships have been loaded into the session context.
   */
  get isLoaded() {
    try {
      return !!this.user.id;
    } catch {
      return false;
    }
  }

  /**
   * Whether the user has been authenticated and has a current role.
   */
  get isFullyAuthenticated() {
    return !!(this.session.isAuthenticated && this.currentRole);
  }

  /**
   * The current role for the user, or `undefined` if none exists.
   * Auto-selects the first role if multiple exist and none has been explicitly selected.
   */
  get currentRole() {
    if (this.roles.length === 0) return undefined;
    if (this.roles.length === 1) return this.roles[0];

    // If a role was previously selected (from localStorage), use it
    const savedRole = this.roles.find((role) => role.relationshipType === this.currentRoleId);
    if (savedRole) return savedRole;

    // Otherwise auto-select first role and persist it
    const firstRole = this.roles[0];
    if (firstRole) {
      localStorage.setItem('role', firstRole.relationshipType);
    }
    return firstRole;
  }

  /**
   * The current employee for the user, or an empty employee object if none
   * exists.
   */
  // In an active demo session, `currentEmployee`/`person` derive from the
  // demo `/me` user (seeded by the demo fetch interceptor) exactly like
  // production — no bespoke per-getter demo swap.
  @cached
  get currentEmployee(): EmployeeModel {
    if (!this.user?.id || !this.user.employees?.length) {
      return this.store.createRecord<EmployeeModel>('employee', {});
    }
    return (
      this.user.employees.find(
        (employee: EmployeeModel) => employee.company.id === this.currentRole?.company?.id
      ) || this.store.createRecord<EmployeeModel>('employee', {})
    );
  }

  @cached
  get person() {
    return this.user.person || this.store.createRecord<PersonModel>('person', {});
  }

  /**
   * Gets the subdomain from the current hostname.
   * Uses shared hostname utility for consistency across services.
   */
  get subdomain() {
    return getSubdomain();
  }

  /**
   * Gets the company logo URL.
   * For non-localhost environments, uses subdomain to construct logo path.
   * For localhost, uses the first employee's company subdomain.
   */
  get logo() {
    if (isLocalhost()) {
      // In localhost, get subdomain from first employee's company setting
      const firstEmployee = this.user.employees?.[0];
      const companySubdomain = firstEmployee?.company?.companySetting?.subDomain;
      return getPartnerLogoUrl(companySubdomain, ENV.tioEnvironment);
    }

    // For non-localhost, use the subdomain from the URL
    return getPartnerLogoUrl(this.subdomain, ENV.tioEnvironment);
  }

  /**
   * Whether the user is viewing the app as an employee.
   */
  get isEmployeeView() {
    return this.currentRole?.relationshipType === 'PARTICIPANT' || this.isFamilyMember;
  }

  /**
   * Whether the user is viewing the app as a partner admin.
   */
  get isAdminView() {
    if (this.isTasRole) {
      return true;
    }

    if (!this.currentRole?.relationshipType) {
      return false;
    }

    return [
      'ACCOUNT_OWNER',
      'EMPLOYEE_ADMIN',
      'PSLF_ADMIN',
      'TIO_ACCOUNT_MANAGER',
      'TIO_ADMIN',
    ].includes(this.currentRole?.relationshipType);
  }

  get isTasRole() {
    if (!this.currentRole?.relationshipType) {
      return false;
    }

    return (
      this.currentRole.relationshipType.startsWith('TAS.Approver') ||
      this.currentRole.relationshipType === 'TAS.Admin.SUPERUSER'
    );
  }

  get isTasTioApprover() {
    if (!this.currentRole?.relationshipType) {
      return false;
    }

    return this.currentRole.relationshipType === 'TAS.Approver.TIO_APPROVER';
  }

  get canEditTasApplication() {
    if (!this.currentRole?.relationshipType) {
      return false;
    }
    const approvedRoles = [
      'TAS.Admin.PROGRAM_ADMIN',
      'TAS.Admin.SUPERUSER',
      'TAS.Approver.ADMIN_APPROVER',
      'TAS.Approver.APPROVER',
      'TAS.Approver.CUSTOM_APPROVER',
      'TAS.Approver.FINANCE_APPROVER',
      'TAS.Approver.HYPERVISOR',
      'TAS.Approver.PPP_FINANCE_APPROVER',
      'TAS.Approver.TAP_FINANCE_APPROVER',
      'TAS.Approver.TIO_APPROVER',
      'TAS.Approver.SUPERVISOR',
      'TIO_ACCOUNT_MANAGER',
      'TIO_ADMIN',
      'EMPLOYEE_ADMIN',
      'ACCOUNT_OWNER',
    ];
    return approvedRoles.includes(this.currentRole.relationshipType);
  }

  get isPslfAdmin() {
    return this.currentRole?.relationshipType === 'PSLF_ADMIN';
  }

  get isPslfGroupApprover() {
    const relationships = this.currentEmployee.jurisdictiveRelationships;
    return relationships.some((jr) => jr.relationshipType === 'PSLF_GROUP_APPROVER');
  }

  get isFamilyMember() {
    return this.currentRole?.role === 'FAMILY_MEMBER';
  }

  get isEmailProviderLogin() {
    const userLogin = this.user.logins?.[0];
    return userLogin?.provider === 'EMAIL';
  }

  get isOtherProviderLogin() {
    if (!this.user.id) {
      return false;
    }
    return !this.isEmailProviderLogin;
  }

  get hasAcceptedAppTerms() {
    return !!(
      this.user.usageAgreement &&
      this.user.privacyAgreement &&
      this.hasAcceptedAllCustomRegisterTerms
    );
  }

  get needsToAcceptAppTerms() {
    return this.user && !this.hasAcceptedAppTerms;
  }

  @cached
  get hasAcceptedAllCustomRegisterTerms() {
    const customRegisterAgreementTermsIds = new Set(
      // @ts-expect-error: seems like relation is typed wrong
      (this.user.customRegisterAgreements || []).map((a: AgreementModel) => a.condition?.id)
    );
    const customRegisterTermIds = (this.preRegister.customTerms || []).map((t) => t.id);

    if (customRegisterAgreementTermsIds.size < customRegisterTermIds.length) {
      return false;
    }
    return customRegisterTermIds.every((termId) => {
      return customRegisterAgreementTermsIds.has(termId);
    });
  }

  get isParticipantOnly() {
    return this.roles?.length === 1 && this.roles[0]?.relationshipType === 'PARTICIPANT';
  }

  /**
   * Loads the user data for the current authenticated user.
   */
  load = async () => {
    // WarpDrive bootstrap (Phase 2.5): /me through store.request replaces the
    // raw fetch + pushPayload + peekRecord dance — the document materializes in
    // the cache and content.data IS the user record. Headers (x-api-key,
    // tio-auth-token, Time-Zone, X-Request-Id) are applied by the request
    // manager's TioAuthRequestHandler, so none are needed here.
    let content: { data: UserModel };
    try {
      ({ content } = (await this.store.request(meRequest(meIncludes))) as {
        content: { data: UserModel };
      });
    } catch (e) {
      const status = (e as { status?: number })?.status;
      // Parity with the legacy bootstrap: 401/403/404 on /me means the
      // session is unusable — call logout so the caller can redirect.
      if (status && [401, 403, 404].includes(status)) {
        this.logout();
        throw new Error(`Unauthorized status loading user data: ${status}`, { cause: e });
      }
      throw e;
    }
    this.user = content.data;
    this.roles = trackedArray([...this.user.uniqueRolesByRelationshipType]);
    if (this.isFamilyMember) {
      // After store.request the included company records are materialized in
      // the cache; for a family-member user the sponsoring company is
      // reachable via the first sideloaded employee's company relationship.
      const chosenCompany = content.data.employees?.[0]?.company;
      if (chosenCompany) {
        this.company = chosenCompany;
      }
    }
  };

  /**
   * Returns the employee IDs fo the current user.
   *
   * HACK: We are struggling with hard-to-reproduce timing bugs that are causing
   *       `sessionContext.user.person.employees` to evaluate as empty
   *       unexpectedly.
   */
  getEmployeeIdsAsync = async () => {
    let employees = this.user?.person?.employees || [];

    if (!employees.length) {
      // WarpDrive builder read (Phase 2.4) — replaces store.findRecord;
      // reload: true maps to the builder's reload option.
      const { content } = (await this.store.request(
        findRecord<UserModel>('user', this.user.id!, {
          include: 'person.employees',
          reload: true,
        })
      )) as { content: { data: UserModel } };
      employees = content.data.person?.employees || [];
    }
    return employees.map((e) => e.id);
  };

  /**
   * Sets the current role for the authenticated user.
   *
   * @param {RoleView} role - A user role
   */
  setCurrentRole = (role: RoleViewModel) => {
    this.actualCurrentRelationshipType = role.relationshipType;
    localStorage.setItem('role', role.relationshipType);
    if (this.isEmployeeView) {
      this.router.transitionTo('authenticated.dashboard');
    } else {
      this.router.transitionTo('authenticated.admin');
    }

    try {
      setUserProperty('role', this.currentRole?.role);
      setUserProperty('relationshipType', this.currentRole?.relationshipType);
    } catch (e) {
      console.error('Error updating DataDog user current role', e);
    }
  };

  /**
   * Destroys the authenticated session and logs out the current user.
   */
  logout = async () => {
    try {
      this.zendesk.clearUser();
    } catch (e) {
      console.error('[service:session-context] Could not clear zendesk user', e);
    }

    try {
      clearUser();
    } catch (e) {
      console.error('[service:session-context] Could not clear datadog user', e);
    }

    // This must be last, since this causes a redirect once it is executed
    try {
      await this.session.invalidate();
    } catch (e) {
      console.error('[service:session-context] Could not clear session context', e);
    }

    localStorage.removeItem('role');
    sessionStorage.clear();

    // Demo mode is sticky in localStorage; clear it on logout so a demo session
    // never survives session invalidation (nor leaks to the next user on a
    // shared device). clearDemoSession also drops the overloaded `role` key.
    try {
      clearDemoSession();
    } catch (e) {
      console.error('[service:session-context] Could not clear demo session', e);
    }

    try {
      this.roles = [];
      this.user = new UserModel();
    } catch (e) {
      console.error('[service:session-context] Could not clear user and roles', e);
    }
  };
}

export default SessionContextService;
